import { useState } from "react";

import { Announcer, ConnectDomain, EmailDomainStatus, EntitlementNotice } from "@/components/email/parts";
import { ErrorState, Spinner, type Requirement } from "@/components/ui";
import type { DomainDetail } from "@/lib/domains";
import { useEmailOverview } from "@/lib/email";

/**
 * Professional e-mail for one domain: whether its MX, SPF, DKIM and DMARC records are right, when DNS was last
 * read, what to do about each one, and what the plan allows. The server decides everything (permissions,
 * entitlement, whether a mailbox may be created); this screen only explains it. The same pieces make up the
 * Email page (for people who manage e-mail but cannot open the Domains pages).
 */
export function EmailTab({ detail }: { detail: DomainDetail }) {
  const overview = useEmailOverview();
  const [announcement, setAnnouncement] = useState("");
  const manage: Requirement = { permission: "email:manage", on: ["domain_folder", detail.folder?.id] };

  if (overview.isPending) return <Spinner />;
  if (overview.isError) return <ErrorState error={overview.error} onRetry={() => void overview.refetch()} />;
  const { domains, providers, entitlement } = overview.data;
  const row = domains.find((d) => d.domain_id === detail.domain.id);
  const fqdn = detail.domain.display_name;

  return (
    <div className="flex flex-col gap-5">
      <EntitlementNotice entitlement={row?.entitlement ?? entitlement} />
      {row ? (
        <EmailDomainStatus row={row} onAnnounce={setAnnouncement} />
      ) : (
        <ConnectDomain
          choices={[{ domain_id: detail.domain.id, fqdn }]}
          providers={providers}
          entitlement={entitlement}
          requires={manage}
          onAnnounce={setAnnouncement}
          subtitle={`Email addresses on ${fqdn}`}
        />
      )}
      <Announcer message={announcement} />
    </div>
  );
}
